'use client'

import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { filterModalType } from '@/lib/page/type'
import { serviceType } from '@/lib/service/server'
import { SlidersHorizontal } from 'lucide-react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useState } from 'react'

type FilterModalProps = {
  service: serviceType
  filterModal: filterModalType
}

type SelectedFilters = {
  category_id: number | null
  color_ids: number[]
  size_ids: number[]
  tag_id: number | null
  price_range: [number, number] | null
}

const FilterModalInner: React.FC<FilterModalProps> = ({
  service,
  filterModal,
}) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    category_id: null,
    color_ids: [],
    size_ids: [],
    tag_id: null,
    price_range: null,
  })
  const [isOpen, setIsOpen] = useState(false)

  // URLパラメータから初期値を設定
  useEffect(() => {
    const categoryId = searchParams.get('category_id')
    const colorIds = searchParams.getAll('color_ids').map(Number)
    const sizeIds = searchParams.getAll('size_ids').map(Number)
    const tagId = searchParams.get('tag_id')
    const priceRange = searchParams
      .get('price_range')
      ?.split(',')
      .map(Number) as [number, number] | null

    setSelectedFilters({
      category_id: categoryId ? Number(categoryId) : null,
      color_ids: colorIds,
      size_ids: sizeIds,
      tag_id: tagId ? Number(tagId) : null,
      price_range: priceRange && priceRange.length === 2 ? priceRange : null,
    })
  }, [searchParams])

  // 選択状態をトグルする関数
  const toggleSelection = (type: keyof SelectedFilters, id: number) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev }

      if (type === 'category_id') {
        // categoryは単一選択
        newFilters.category_id = newFilters.category_id === id ? null : id
      } else if (type === 'tag_id') {
        // tagは単一選択
        newFilters.tag_id = newFilters.tag_id === id ? null : id
      } else if (type === 'price_range') {
        switch (id) {
          case 1:
            newFilters.price_range = [0, 1000]
            break
          case 2:
            newFilters.price_range = [1000, 2000]
            break
          case 3:
            newFilters.price_range = [2000, 3000]
            break
          case 4:
            newFilters.price_range = [3000, 4000]
            break
          case 5:
            newFilters.price_range = [4000, 5000]
            break
          case 6:
            newFilters.price_range = [5000, 6000]
            break
          case 7:
            newFilters.price_range = [6000, 7000]
            break
          case 8:
            newFilters.price_range = [7000, 8000]
            break
          case 9:
            newFilters.price_range = [8000, 9000]
            break
          case 10:
            newFilters.price_range = [9000, 10000]
            break
          case 11:
            newFilters.price_range = [10000, 99999]
            break
        }
      } else {
        // color_ids, size_idsは複数選択
        const currentIds = newFilters[type] as number[]
        if (currentIds.includes(id)) {
          newFilters[type] = currentIds.filter(
            (existingId) => existingId !== id,
          ) as any
        } else {
          newFilters[type] = [...currentIds, id] as any
        }
      }
      return newFilters
    })
  }

  // 選択状態をチェックする関数
  const isSelected = (type: keyof SelectedFilters, id: number) => {
    if (type === 'category_id') {
      return selectedFilters.category_id === id
    } else if (type === 'tag_id') {
      return selectedFilters.tag_id === id
    } else if (type === 'price_range') {
      if (!selectedFilters.price_range) return false
      switch (id) {
        case 1:
          return (
            selectedFilters.price_range[0] === 0 &&
            selectedFilters.price_range[1] === 1000
          )
        case 2:
          return (
            selectedFilters.price_range[0] === 1000 &&
            selectedFilters.price_range[1] === 2000
          )
        case 3:
          return (
            selectedFilters.price_range[0] === 2000 &&
            selectedFilters.price_range[1] === 3000
          )
        case 4:
          return (
            selectedFilters.price_range[0] === 3000 &&
            selectedFilters.price_range[1] === 4000
          )
        case 5:
          return (
            selectedFilters.price_range[0] === 4000 &&
            selectedFilters.price_range[1] === 5000
          )
        case 6:
          return (
            selectedFilters.price_range[0] === 5000 &&
            selectedFilters.price_range[1] === 6000
          )
        case 7:
          return (
            selectedFilters.price_range[0] === 6000 &&
            selectedFilters.price_range[1] === 7000
          )
        case 8:
          return (
            selectedFilters.price_range[0] === 7000 &&
            selectedFilters.price_range[1] === 8000
          )
        case 9:
          return (
            selectedFilters.price_range[0] === 8000 &&
            selectedFilters.price_range[1] === 9000
          )
        case 10:
          return (
            selectedFilters.price_range[0] === 9000 &&
            selectedFilters.price_range[1] === 10000
          )
        case 11:
          return (
            selectedFilters.price_range[0] >= 10000 &&
            selectedFilters.price_range[1] <= 99999
          )
      }
    } else {
      return (selectedFilters[type] as number[]).includes(id)
    }
  }

  // 検索を実行する関数
  const handleSearch = () => {
    const params = new URLSearchParams()

    // 各フィルターをクエリパラメータに追加
    if (selectedFilters.category_id) {
      params.append('category_id', selectedFilters.category_id.toString())
    }

    selectedFilters.color_ids.forEach((id) => {
      params.append('color_ids', id.toString())
    })

    selectedFilters.size_ids.forEach((id) => {
      params.append('size_ids', id.toString())
    })

    if (selectedFilters.tag_id) {
      params.append('tag_id', selectedFilters.tag_id.toString())
    }

    if (
      selectedFilters.price_range &&
      selectedFilters.price_range.length === 2
    ) {
      params.append('price_from', selectedFilters.price_range[0].toString())
      params.append('price_to', selectedFilters.price_range[1].toString())
    }

    // 商品一覧ページに遷移
    router.push(`/itemlist?${params.toString()}`)
    setIsOpen(false)
  }

  // フィルターをリセットする関数
  const handleReset = () => {
    setSelectedFilters({
      category_id: null,
      color_ids: [],
      size_ids: [],
      tag_id: null,
      price_range: null,
    })
  }

  if (filterModal.components.length === 0) return null

  return (
    <div className={`filter-modal py-4 w-full`}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger
          className={`filter-modal-trigger button-color-reverse main-color-reverse text-white font-semibold p-2 items-center hover:bg-opacity-90 transition-all duration-200 w-[60%] mx-auto text-center rounded-lg flex justify-center`}
        >
          <SlidersHorizontal className="mr-2" />
          <p className="">{filterModal.title}</p>
        </DialogTrigger>
        <DialogContent className="filter-modal-content p-6 bg-white rounded-md max-w-md mx-auto max-h-[80vh] overflow-y-auto w-[90%]">
          <div className="space-y-6">
            {filterModal.components.map((component, index) => (
              <div key={index} className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {component.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {component.elements.map((element) => {
                    const filterType =
                      component.type === 'category'
                        ? 'category_id'
                        : component.type === 'color'
                          ? 'color_ids'
                          : component.type === 'size'
                            ? 'size_ids'
                            : component.type === 'price'
                              ? 'price_range'
                              : 'tag_id'

                    const selected = isSelected(filterType, element.id)

                    return (
                      <button
                        key={element.id}
                        onClick={() => toggleSelection(filterType, element.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                          selected
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {element.name}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
            <div className="border-t border-gray-200"></div>
            {/* ボタン */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleReset}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
              >
                リセット
              </button>
              <button
                onClick={handleSearch}
                className="flex-1 px-4 py-3 text-white bg-gray-900 rounded-lg font-medium hover:bg-gray-800 transition-all duration-200"
                style={{ backgroundColor: service.main_color }}
              >
                検索
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const FilterModal: React.FC<FilterModalProps> = (props) => {
  return (
    <Suspense fallback={null}>
      <FilterModalInner {...props} />
    </Suspense>
  )
}
